import { useState, useCallback, useRef } from 'react'
import { Room, RoomEvent, Track, LocalTrackPublication } from 'livekit-client'

const DEBUG_TAG = '[VideoCallRecording]'

/**
 * ビデオ通話の音声ミックス録音 hook
 *
 * Web Audio API で薬剤師（ローカル）と患者（リモート）の音声をミックスし、
 * MediaRecorder で録音する。
 *
 * LiveKit のトラック publish は非同期のため、録音開始時にトラックが存在しない
 * 場合がある。LocalTrackPublished / TrackSubscribed イベントを監視して
 * 遅延 publish されたトラックも動的にミックスに追加する。
 */
export function useVideoCallRecording(room: Room | undefined) {
  const [isRecording, setIsRecording] = useState(false)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)

  const audioCtxRef = useRef<AudioContext | null>(null)
  const destRef = useRef<MediaStreamAudioDestinationNode | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const listenersRef = useRef<{
    onTrackSubscribed:
      | ((track: { kind: string; mediaStreamTrack: MediaStreamTrack }) => void)
      | null
    onLocalTrackPublished: ((pub: LocalTrackPublication) => void) | null
  }>({ onTrackSubscribed: null, onLocalTrackPublished: null })

  /** AudioContext の destination にオーディオソースを接続するヘルパー */
  const connectTrackToMix = (
    mediaStreamTrack: MediaStreamTrack,
    label: string
  ) => {
    const audioCtx = audioCtxRef.current
    const dest = destRef.current
    if (!audioCtx || !dest || audioCtx.state === 'closed') {
      console.warn(
        DEBUG_TAG,
        'Cannot connect track, AudioContext unavailable:',
        label
      )
      return
    }
    console.log(
      DEBUG_TAG,
      'Connecting track to mix:',
      label,
      'readyState:',
      mediaStreamTrack.readyState
    )
    const source = audioCtx.createMediaStreamSource(
      new MediaStream([mediaStreamTrack])
    )
    source.connect(dest)
  }

  const startRecording = useCallback(() => {
    if (!room) {
      console.warn(DEBUG_TAG, 'startRecording called but room is undefined')
      return
    }

    // 二重録音を防止
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      console.warn(DEBUG_TAG, 'Already recording, skipping')
      return
    }

    console.log(DEBUG_TAG, 'startRecording: creating AudioContext')
    const audioCtx = new AudioContext()
    audioCtxRef.current = audioCtx
    const dest = audioCtx.createMediaStreamDestination()
    destRef.current = dest

    // 既に publish 済みのローカルマイクを接続
    const localTrack = room.localParticipant.getTrackPublication(
      Track.Source.Microphone
    )
    if (localTrack?.track) {
      connectTrackToMix(localTrack.track.mediaStreamTrack, 'local-mic')
    } else {
      console.log(
        DEBUG_TAG,
        'Local mic not yet published, will connect via event'
      )
    }

    // 既に subscribe 済みのリモート音声を接続
    let remoteTrackCount = 0
    room.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((pub) => {
        if (pub.track && pub.track.kind === Track.Kind.Audio) {
          connectTrackToMix(
            pub.track.mediaStreamTrack,
            'remote-' + participant.identity
          )
          remoteTrackCount++
        }
      })
    })
    console.log(DEBUG_TAG, 'Initial remote tracks:', remoteTrackCount)

    // ローカルトラック publish 時にミックスに追加
    const onLocalTrackPublished = (pub: LocalTrackPublication) => {
      if (pub.source === Track.Source.Microphone && pub.track) {
        console.log(DEBUG_TAG, 'Local mic published, connecting to mix')
        connectTrackToMix(pub.track.mediaStreamTrack, 'local-mic-late')
      }
    }
    room.localParticipant.on('localTrackPublished', onLocalTrackPublished)

    // リモート音声 subscribe 時にミックスに追加
    const onTrackSubscribed = (track: {
      kind: string
      mediaStreamTrack: MediaStreamTrack
    }) => {
      if (track.kind === Track.Kind.Audio) {
        connectTrackToMix(track.mediaStreamTrack, 'remote-subscribed')
      }
    }
    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed)

    listenersRef.current = { onTrackSubscribed, onLocalTrackPublished }

    // MediaRecorder で録音
    chunksRef.current = []
    const recorder = new MediaRecorder(dest.stream, {
      mimeType: 'audio/webm;codecs=opus',
    })
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data)
      }
    }
    recorder.onerror = (e) => {
      console.error(DEBUG_TAG, 'MediaRecorder error:', e)
    }
    recorder.onstop = () => {
      console.log(DEBUG_TAG, 'onstop: chunks=', chunksRef.current.length)
      audioCtxRef.current?.close()
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
      console.log(DEBUG_TAG, 'Blob size:', blob.size)
      // 0バイトの blob はアップロードしない（トラック接続前に終了した場合）
      setAudioBlob(blob.size > 0 ? blob : null)
    }
    recorder.start(1000)
    recorderRef.current = recorder
    setIsRecording(true)
    console.log(DEBUG_TAG, 'Recording started')
  }, [room])

  const stopRecording = useCallback(() => {
    console.log(
      DEBUG_TAG,
      'stopRecording: recorder=',
      recorderRef.current?.state,
      'chunks=',
      chunksRef.current.length
    )
    recorderRef.current?.stop()
    // イベントリスナーのクリーンアップ
    if (room) {
      const { onTrackSubscribed, onLocalTrackPublished } = listenersRef.current
      if (onTrackSubscribed) {
        room.off(RoomEvent.TrackSubscribed, onTrackSubscribed)
      }
      if (onLocalTrackPublished) {
        room.localParticipant.off('localTrackPublished', onLocalTrackPublished)
      }
      listenersRef.current = {
        onTrackSubscribed: null,
        onLocalTrackPublished: null,
      }
    }
    setIsRecording(false)
  }, [room])

  return {
    isRecording,
    startRecording,
    stopRecording,
    audioBlob,
  }
}
